'use client';

import { useRef } from 'react';
import { Container, Row, Col } from '@geberit/gdds';

// styles
import { CatalogWrapper } from './online-catalog-assets-wrapper.styles';

// types
import type { Props } from './online-catalog-assets.types';
import { Formats } from 'components/ContentElementsGdds/headline/headlines.types';

// components
import { Headline } from 'components/ContentElementsGdds/headline/headline';
import OkvTilesView from './okv-tiles-view';
import AccordionView from './accordion-view';

// utils
import { gridSizes, buildSize } from 'utils/gridSize';
import { previewId } from 'utils/preview-id';
import { useSectionId } from 'utils/hooks/use-section-id';
import { isEmpty } from 'utils/is-empty';
import { useIsCC } from 'utils/hooks/use-is-cc';
import { useFetchAssets } from './useFetchAssets';

export function OnlineCatalogAssets(props: Readonly<Props>) {
  const {
    title,
    anchor,
    subtitle,
    contentIndex,
    titleInAnchor,
    catalogName,
    isSectionReference,
    data,
    hideCount,
    backgroundColor,
    carousel,
    text,
    serverProps,
  } = props;
  const isCC = useIsCC();

  const clientData = useFetchAssets({ enabled: isCC && !serverProps, data, hideCount });

  const okvTiles = isCC && !serverProps ? clientData.data : serverProps?.data;

  const isAccordionView = data.length > 1;
  const accordionRef = useRef(null);

  const renderOkvAccordion = () => (
    <AccordionView items={okvTiles} carousel={carousel} backgroundColor={backgroundColor} />
  );

  const renderOkvTiles = () =>
    !isEmpty(okvTiles[0]?.items) && (
      <OkvTilesView
        items={okvTiles[0]?.items}
        carousel={carousel}
        backgroundColor={backgroundColor}
        size={[4, 4, 4]}
      />
    );

  return (
    <CatalogWrapper
      backgroundColor={backgroundColor}
      isAccordion={isAccordionView}
      {...previewId(catalogName && !isSectionReference ? `#${catalogName}` : undefined)}
      id={useSectionId(title ?? '', anchor ?? '', titleInAnchor ?? false, contentIndex)}
      ref={accordionRef}
    >
      {(title || subtitle || text) && (
        <Container maxContentWidth={buildSize(gridSizes.gddsFullGrid)}>
          <Row>
            <Col size={[4, 8, 12]}>
              <Headline
                title={title}
                subtitle={subtitle}
                tag={Formats.h2}
                text={text}
                textClassName="copy-text"
              />
            </Col>
          </Row>
        </Container>
      )}
      {isAccordionView ? renderOkvAccordion() : renderOkvTiles()}
    </CatalogWrapper>
  );
}
